import React, { useEffect, useMemo } from 'react';
import { SensorsKPITabContainer } from './SensorsKPITab.styles';
import { observer } from 'mobx-react';
import { useStores } from '@/hooks/use-stores';
import KPISensorCard from './KPISensorCard';

import {
  getSensorPriority,
  getSensorOptions,
  handleSaveDisabled,
  FLOW_TYPE,
  PRESSURE_TYPE,
  SensorsKPITabProps,
  getSensorsTypeData,
} from './SensorsKPITab.utils';
import { EngineConfigStatus } from '@/store/Engine/constants';

const SensorsKPITab = observer(({ setIsSaveDisabled, activeTab, isPressureFallbackAllowed, isFlowFallbackAllowed }: SensorsKPITabProps) => {
  const { engineStore, devicesStore } = useStores();

  const flowSensorsPriority = useMemo(
    () => getSensorPriority(engineStore.sensorsConfiguration, FLOW_TYPE),
    [engineStore.sensorsConfiguration],
  );

  const pressureSensorsPriority = useMemo(
    () => getSensorPriority(engineStore.sensorsConfiguration, PRESSURE_TYPE),
    [engineStore.sensorsConfiguration],
  );

  const flowSensorsOptions = useMemo(() => getSensorOptions(devicesStore, FLOW_TYPE), [devicesStore]);

  const pressureSensorsOptions = useMemo(() => getSensorOptions(devicesStore, PRESSURE_TYPE), [devicesStore]);

  const isAlwaysReadOnly = useMemo(() => {
    return engineStore.configurationStatus === EngineConfigStatus.Published || engineStore.configurationStatus === EngineConfigStatus.Recommendation;
  }, [engineStore.configurationStatus]);

  useEffect(() => {
    handleSaveDisabled(setIsSaveDisabled, engineStore.sensorsConfiguration);
  }, [setIsSaveDisabled, engineStore.sensorsConfiguration]);

  const sensorsTypeData = getSensorsTypeData(
    flowSensorsPriority,
    flowSensorsOptions,
    pressureSensorsPriority,
    pressureSensorsOptions,
  );

  return (
    <SensorsKPITabContainer $isReadOnly={engineStore.isReadOnly} $activeTab={activeTab}>
      {sensorsTypeData.map(({ sensorsPriority, sensorsOptions, title, type, icon }) => (
        <KPISensorCard
          key={type}
          sensorsPriority={sensorsPriority}
          sensorsOptions={sensorsOptions}
          title={title}
          type={type}
          icon={icon}
          isPressureFallbackAllowed={isPressureFallbackAllowed}
          isFlowFallbackAllowed={isFlowFallbackAllowed}
          setIsSaveDisabled={setIsSaveDisabled}
          hasTriedToSubmit={engineStore.hasTriedToSubmit}
          isAlwaysReadOnly={isAlwaysReadOnly}
        />
      ))}
    </SensorsKPITabContainer>
  );
});

export default React.memo(SensorsKPITab);
